import { Slot } from '@radix-ui/react-slot';
import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '../lib/cn';
import type { IconComponent } from '../lib/types';

export interface NavRailProps {
  /** The accessible name of the navigation landmark. */
  label: string;
  /** Icons only, labels kept for assistive technology and as a tooltip. */
  compact?: boolean;
  children: ReactNode;
  className?: string | undefined;
}

/**
 * The product's primary navigation (P1-MVP-06, P1-MVP-07), as the Brand DNA's
 * interface concepts draw it: a charcoal rail, the current section on forest
 * with a mint bar.
 */
export function NavRail({ label, compact = false, children, className }: NavRailProps) {
  return (
    <nav
      aria-label={label}
      data-compact={compact || undefined}
      className={cn('group/rail flex flex-col gap-1 p-2', className)}
    >
      <ul className="flex flex-col gap-1">{children}</ul>
    </nav>
  );
}

export interface NavRailItemProps extends ComponentPropsWithRef<'a'> {
  icon: IconComponent;
  label: string;
  current?: boolean;
  /**
   * Render the child — a router's `<Link>` — instead of an `<a>`, so the rail
   * does not depend on a router.
   */
  asChild?: boolean;
}

export function NavRailItem({
  icon: Icon,
  label,
  current = false,
  asChild = false,
  className,
  children,
  ...props
}: NavRailItemProps) {
  const Component = asChild ? Slot : 'a';

  return (
    <li>
      <Component
        aria-current={current ? 'page' : undefined}
        title={label}
        className={cn(
          'relative flex h-10 items-center gap-3 rounded-md px-3 text-sm font-medium',
          'transition-colors duration-(--pl-duration-fast)',
          current
            ? 'bg-accent-soft text-fg before:absolute before:inset-y-2 before:left-0 before:w-1 before:rounded-full before:bg-accent'
            : 'text-fg-muted hover:bg-surface-raised hover:text-fg',
          'group-data-[compact]/rail:justify-center group-data-[compact]/rail:px-0',
          className,
        )}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            <Icon aria-hidden className={cn('size-5 shrink-0', current && 'text-accent')} />
            <span className="truncate group-data-[compact]/rail:sr-only">{label}</span>
          </>
        )}
      </Component>
    </li>
  );
}

/**
 * The inside of an item rendered `asChild`: the same icon and label the plain
 * item draws, for a consumer's `<Link>` to wrap.
 */
export function NavRailItemContent({
  icon: Icon,
  label,
  current = false,
}: {
  icon: IconComponent;
  label: string;
  current?: boolean;
}) {
  return (
    <>
      <Icon aria-hidden className={cn('size-5 shrink-0', current && 'text-accent')} />
      <span className="truncate group-data-[compact]/rail:sr-only">{label}</span>
    </>
  );
}
